import { collection, deleteField, doc, Firestore, limit, onSnapshot, query, where, writeBatch } from 'firebase/firestore';

import { createArc, saveArcDetails, subscribeArcDetails } from './arcs';
import { synchronizePublishedNovel } from './publication-sync';

import type { NarrativeStore } from '../contracts/narrative';
import type {
  Arc,
  ArcDetails,
  NarrativeSummary,
  NarrativeType,
  Novel,
  NovelStorageLayout,
  Scene,
  ScenePack,
  SceneSnapshot,
  StructureSection
} from '@/lib/types';
import type { Functions } from 'firebase/functions';

import { NarrativeDao } from '@/api/daos/narrative-dao';
import { StorageResolver } from '@/firebase/firestore/storage-resolver';
import { normalizeDates } from '@/lib/date-utils';



export class FirebaseNarrativeStore implements NarrativeStore {
  constructor(private firestore: Firestore, private functions: Functions) { }

  subscribeArcDetails(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    arcId: string,
    onUpdate: (details: ArcDetails | null) => void,
    onError: (error: Error) => void
  ): () => void {
    return subscribeArcDetails(this.firestore, ownerId, novelId, layout, arcId, onUpdate, onError);
  }

  async saveArcDetails(
    ownerId: string,
    novel: Novel,
    arcId: string,
    details: Partial<ArcDetails>
  ): Promise<void> {
    return saveArcDetails(this.firestore, ownerId, novel, arcId, details);
  }

  async createArc(
    ownerId: string,
    novel: Novel,
    arc: Arc
  ): Promise<void> {
    return createArc(this.firestore, ownerId, novel, arc);
  }

  subscribeNarrativeItem<T extends { id: string }>(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    narrativeType: NarrativeType,
    id: string,
    onUpdate: (item: T | null) => void,
    onError: (error: Error) => void
  ): () => void {
    const resolver = new StorageResolver(this.firestore);
    const { ref, fieldPath } = resolver.resolveItem(ownerId, novelId, 'narrative', narrativeType, id, layout);

    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          onUpdate(null);
          return;
        }
        if (fieldPath) {
          const list = (snap.data() as { entityList?: Record<string, T> }).entityList || {};
          const item = list[id] || null;
          onUpdate(item ? normalizeDates({ ...item, id }) : null);
        } else {
          onUpdate(normalizeDates({ ...(snap.data() as T), id }));
        }
      },
      onError
    );
  }

  subscribeNarrativeItems<T extends { id: string }>(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    narrativeType: NarrativeType,
    onUpdate: (items: T[]) => void,
    onError: (error: Error) => void
  ): () => void {
    const resolver = new StorageResolver(this.firestore);
    const mode = layout?.narrative?.[narrativeType]?.items || 'embedded';

    if (mode === 'split') {
      const collRef = collection(resolver.getNovelRef(ownerId, novelId), narrativeType);
      return onSnapshot(
        collRef,
        (snap) => {
          onUpdate(snap.docs.map(d => normalizeDates({ ...(d.data() as T), id: d.id })));
        },
        onError
      );
    } else {
      const bucketRef = resolver.getBucketRef(ownerId, novelId, 'narrative', narrativeType);
      return onSnapshot(
        bucketRef,
        (snap) => {
          if (snap.exists()) {
            const list = (snap.data() as { entityList?: Record<string, T> }).entityList || {};
            onUpdate(Object.entries(list).map(([id, item]) => normalizeDates({
              ...item,
              id: item.id || id,
            })));
          } else {
            onUpdate([]);
          }
        },
        onError
      );
    }
  }

  subscribeStoryboardForScene<T extends { id: string }>(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    sceneId: string,
    onUpdate: (storyboards: T[]) => void,
    onError: (error: Error) => void
  ): () => void {
    const resolver = new StorageResolver(this.firestore);
    const mode = layout?.narrative?.storyboards?.items || 'embedded';

    if (mode === 'split') {
      const collRef = collection(resolver.getNovelRef(ownerId, novelId), 'storyboards');
      const q = query(collRef, where('sceneId', '==', sceneId), limit(1));
      return onSnapshot(
        q,
        (snap) => {
          onUpdate(snap.docs.map(d => normalizeDates({ ...(d.data() as T), id: d.id })));
        },
        onError
      );
    } else {
      const bucketRef = resolver.getBucketRef(ownerId, novelId, 'narrative', 'storyboards');
      return onSnapshot(
        bucketRef,
        (snap) => {
          if (snap.exists()) {
            const list = (snap.data() as { entityList?: Record<string, T> }).entityList || {};
            const filtered = Object.values(list).filter((item: any) => item.sceneId === sceneId);
            onUpdate(filtered.map(item => normalizeDates({ ...item })));
          } else {
            onUpdate([]);
          }
        },
        onError
      );
    }
  }

  async getItem<T>(
    ownerId: string,
    novel: Novel,
    family: 'entities' | 'narrative',
    type: string,
    itemId: string
  ): Promise<(T & { id: string }) | null> {
    const narrativeDao = new NarrativeDao(this.firestore);
    return narrativeDao.getItem<T>(ownerId, novel, family, type, itemId);
  }

  async updateNarrativeItem(
    ownerId: string,
    novel: Novel,
    narrativeType: NarrativeType,
    itemId: string,
    updates: Record<string, unknown>
  ): Promise<void> {
    const narrativeDao = new NarrativeDao(this.firestore);
    await narrativeDao.updateNarrativeItem(ownerId, novel, narrativeType, itemId, updates);
    if (narrativeType === 'chapters') await synchronizePublishedNovel(this.functions, ownerId, novel.id);
  }

  async setNarrativeItem<T extends { id: string }>(
    ownerId: string,
    novel: Novel,
    narrativeType: NarrativeType,
    item: T
  ): Promise<void> {
    const narrativeDao = new NarrativeDao(this.firestore);
    await narrativeDao.setNarrativeItem(ownerId, novel, narrativeType, item);
    if (narrativeType === 'chapters') await synchronizePublishedNovel(this.functions, ownerId, novel.id);
  }

  async updateScenePack(
    ownerId: string,
    novel: Novel,
    sceneId: string,
    scene: Partial<ScenePack>
  ): Promise<void> {
    const narrativeDao = new NarrativeDao(this.firestore);
    await narrativeDao.updateScenePack(ownerId, novel, sceneId, scene);
  }

  async deleteNarrativeItem(
    ownerId: string,
    novel: Novel,
    narrativeType: NarrativeType,
    itemId: string
  ): Promise<void> {
    const narrativeDao = new NarrativeDao(this.firestore);
    await narrativeDao.deleteNarrativeItem(ownerId, novel, narrativeType, itemId);
    if (narrativeType === 'chapters') await synchronizePublishedNovel(this.functions, ownerId, novel.id);
  }

  async saveSceneSnapshot(
    ownerId: string,
    novelId: string,
    sceneId: string,
    snapshot: SceneSnapshot
  ): Promise<void> {
    const narrativeDao = new NarrativeDao(this.firestore);
    await narrativeDao.saveSceneSnapshot(ownerId, novelId, sceneId, snapshot);
  }

  async getSceneSnapshot(
    ownerId: string,
    novelId: string,
    sceneId: string,
    snapshotId: string
  ): Promise<SceneSnapshot | null> {
    const narrativeDao = new NarrativeDao(this.firestore);
    return narrativeDao.getSceneSnapshot(ownerId, novelId, sceneId, snapshotId);
  }

  private addSaveToBatch<T extends { id: string }>(
    batch: any,
    resolver: StorageResolver,
    novel: Novel,
    family: 'entities' | 'narrative',
    type: string,
    item: T,
    isNew: boolean = false
  ) {
    const layout = novel.storageLayout;
    const familyLayout = family === 'entities' ? layout.entities : layout.narrative;
    const mode = familyLayout[type]?.items || 'embedded';
    const resolved = resolver.resolveItem(novel.ownerId, novel.id, family, type, item.id, layout);
    const ref = resolved.ref;

    const cleanData: Record<string, unknown> = { ...item };
    Object.keys(cleanData).forEach(k => {
      if (cleanData[k] === undefined) delete cleanData[k];
    });

    if (mode === 'embedded') {
      const { id: _id, ...rest } = cleanData;
      batch.set(
        ref,
        {
          entityList: {
            [item.id]: isNew ? cleanData : rest,
          },
        },
        { merge: true },
      );
    } else {
      if (isNew) {
        batch.set(ref, cleanData);
      } else {
        batch.set(ref, cleanData, { merge: true });
      }
    }
  }

  private addSummaryUpdateToBatch(
    batch: any,
    novel: Novel,
    family: 'entities' | 'narrative',
    type: string,
    itemId: string,
    summary: any
  ) {
    const layout = novel.storageLayout;
    const familyLayout = family === 'entities' ? layout.entities : layout.narrative;
    const metaMode = familyLayout[type]?.meta || 'embedded';
    const metaFamily = family === 'entities' ? 'entitiesMeta' : 'narrativeMeta';

    const cleanSummary: Record<string, unknown> | null = summary
      ? Object.entries(summary).reduce((acc, [key, value]) => {
        if (value !== undefined) acc[key] = value;
        return acc;
      }, {} as Record<string, unknown>)
      : null;

    if (metaMode === 'split') {
      const metaRef = doc(this.firestore, 'users', novel.ownerId, 'novels', novel.id, metaFamily, type);
      batch.set(metaRef, {
        [`summaries.${itemId}`]: cleanSummary || deleteField()
      }, { merge: true });
    } else {
      const novelRef = doc(this.firestore, 'users', novel.ownerId, 'novels', novel.id);
      batch.update(novelRef, {
        [`${metaFamily}.summaries.${type}.${itemId}`]: cleanSummary || deleteField()
      });
    }
  }

  async applySceneSplit(
    ownerId: string,
    novel: Novel,
    originalScene: Scene,
    proposedScenes: Array<{ name: string; goal: string; description: string; eventIds: string[] }>,
    allScenes: NarrativeSummary[]
  ): Promise<void> {
    const batch = writeBatch(this.firestore);
    const resolver = new StorageResolver(this.firestore);
    const now = new Date().toISOString();
    const originalChapterId = originalScene.chapterId || '';
    const sceneId = originalScene.id;

    const originalSummary = novel.summaries?.scenes?.[sceneId] as NarrativeSummary | undefined;
    const startOrder = originalSummary?.order ?? 0;
    const numProposed = proposedScenes.length;

    // 1. Deprecate current scene
    const deprecatedName = `${originalScene.name} (deprecated)`;
    this.addSaveToBatch(batch, resolver, novel, 'narrative', 'scenes', {
      ...originalScene,
      name: deprecatedName,
      isArchived: true,
      audit: {
        ...originalScene.audit,
        updatedAt: now
      }
    });
    this.addSummaryUpdateToBatch(batch, novel, 'narrative', 'scenes', sceneId, {
      ...(originalSummary || { id: sceneId, name: '', updatedAt: now }),
      id: sceneId,
      name: deprecatedName,
      isArchive: true,
      updatedAt: now
    });

    // 2. Identify scenes to shift
    const scenesToShift = allScenes
      .filter(s => s.chapterId === originalChapterId && (s.order || 0) > startOrder && s.id !== sceneId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    // 3. Create new proposed scenes
    let currentOrder = startOrder;
    const { id: _, proposal: __, ...baseSceneData } = originalScene;
    for (const ps of proposedScenes) {
      const newSceneId = doc(collection(this.firestore, 'users', novel.ownerId, 'novels', novel.id, 'scenes')).id;
      const newSceneData = {
        ...baseSceneData,
        id: newSceneId,
        name: ps.name,
        description: ps.description,
        pointOfScene: ps.goal,
        eventIds: ps.eventIds,
        chapterId: originalChapterId,
        order: currentOrder,
        povCharacterId: originalScene.povCharacterId,
        locationIds: originalScene.locationIds,
        organizationIds: originalScene.organizationIds,
        audit: {
          createdAt: now,
          updatedAt: now
        }
      };

      const newSummary: NarrativeSummary = {
        id: newSceneId,
        name: ps.name,
        order: currentOrder,
        chapterId: originalChapterId,
        updatedAt: now,
        status: originalScene.status,
        povCharacterId: originalScene.povCharacterId,
        locationIds: originalScene.locationIds,
        organizationIds: originalScene.organizationIds,
        shortDescription: ps.description,
      };

      this.addSaveToBatch(batch, resolver, novel, 'narrative', 'scenes', newSceneData, true);
      this.addSummaryUpdateToBatch(batch, novel, 'narrative', 'scenes', newSceneId, newSummary);
      currentOrder++;
    }

    // 4. Shift subsequent scenes
    const shiftAmount = numProposed - 1;
    if (shiftAmount > 0) {
      for (const s of scenesToShift) {
        const newOrder = (s.order || 0) + shiftAmount;
        this.addSaveToBatch(batch, resolver, novel, 'narrative', 'scenes', { id: s.id, order: newOrder });
        this.addSummaryUpdateToBatch(batch, novel, 'narrative', 'scenes', s.id, {
          ...s,
          id: s.id,
          order: newOrder,
          updatedAt: now
        });
      }
    }

    await batch.commit();
  }

  async cancelSceneProposal(
    ownerId: string,
    novel: Novel,
    originalScene: Scene
  ): Promise<void> {
    const batch = writeBatch(this.firestore);
    const sceneRef = doc(this.firestore, 'users', novel.ownerId, 'novels', novel.id, 'scenes', originalScene.id);
    batch.update(sceneRef, {
      proposal: deleteField(),
      audit: {
        ...originalScene.audit,
        updatedAt: new Date().toISOString()
      }
    });
    await batch.commit();
  }

  async applyStructureTemplate(
    ownerId: string,
    novel: Novel,
    templateId: string,
    templateSections: Array<{ id: string; name: string; order: number; color: string }>,
    existingSections: StructureSection[]
  ): Promise<void> {
    const batch = writeBatch(this.firestore);
    const resolver = new StorageResolver(this.firestore);
    const layout = novel.storageLayout;
    const mode = layout?.narrative?.structureSections?.items || 'embedded';

    if (mode === 'embedded') {
      const bucketRef = resolver.getBucketRef(ownerId, novel.id, 'narrative', 'structureSections');

      const entityList: Record<string, unknown> = {};

      entityList['unassigned'] = {
        id: 'unassigned',
        templateId: templateId,
        name: 'Unassigned',
        order: -1,
        color: '#94a3b8',
      };

      for (const section of templateSections) {
        entityList[section.id] = {
          id: section.id,
          templateId: templateId,
          name: section.name,
          order: section.order,
          color: section.color,
        };
      }

      batch.set(bucketRef, { entityList });
    } else {
      for (const section of existingSections) {
        const { ref } = resolver.resolveItem(ownerId, novel.id, 'narrative', 'structureSections', section.id, layout);
        if (ref) {
          batch.delete(ref);
        }
      }

      const unassignedRef = resolver.resolveItem(ownerId, novel.id, 'narrative', 'structureSections', 'unassigned', layout).ref;
      batch.set(unassignedRef, {
        id: 'unassigned',
        templateId: templateId,
        name: 'Unassigned',
        order: -1,
        color: '#94a3b8',
      });

      for (const section of templateSections) {
        const sectionRef = resolver.resolveItem(ownerId, novel.id, 'narrative', 'structureSections', section.id, layout).ref;
        batch.set(sectionRef, {
          id: section.id,
          templateId: templateId,
          name: section.name,
          order: section.order,
          color: section.color,
        });
      }
    }

    const novelDocRef = doc(this.firestore, 'users', novel.ownerId, 'novels', novel.id);
    batch.update(novelDocRef, { structureTemplateId: templateId });

    await batch.commit();
  }

  async batchSaveNarrativeItems<T extends { id: string }>(
    ownerId: string,
    novel: Novel,
    narrativeType: NarrativeType,
    items: T[]
  ): Promise<void> {
    const batch = writeBatch(this.firestore);
    const resolver = new StorageResolver(this.firestore);

    for (const item of items) {
      const isNew = !novel.summaries?.[narrativeType]?.[item.id];
      this.addSaveToBatch(batch, resolver, novel, 'narrative', narrativeType, item, isNew);

      const summaryFields = [
        'id', 'name', 'order', 'chapterId', 'povCharacterId',
        'locationIds', 'organizationIds', 'status', 'publishStatus',
        'isArchive', 'date', 'shortDescription', 'color'
      ];
      const summaryUpdates: Record<string, unknown> = {};
      Object.entries(item).forEach(([key, value]) => {
        if (summaryFields.includes(key) && value !== undefined) {
          summaryUpdates[key] = value;
        }
      });

      if (Object.keys(summaryUpdates).length > 0) {
        this.addSummaryUpdateToBatch(batch, novel, 'narrative', narrativeType, item.id, summaryUpdates);
      }
    }

    await batch.commit();
  }
}
